import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ArrowLeft, Heart, MessageCircle, Share, MoreHorizontal, Send, Edit, Trash2, Flag, Copy, ExternalLink, MessageSquare, Bookmark, BookmarkCheck } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';
import { toast } from 'sonner';
import feedHeroBg from '@/assets/feed-hero-bg.jpg';

interface Post {
  id: string;
  user_id: string;
  content: string | null;
  image_url: string | null;
  likes_count: number;
  comments_count: number;
  created_at: string;
  profiles: {
    username: string;
    display_name: string | null;
    avatar_url: string | null;
  } | null;
  pets: {
    name: string;
    avatar_url: string | null;
  } | null;
}

interface Comment {
  id: string;
  user_id: string;
  content: string;
  created_at: string;
  profiles: {
    username: string;
    display_name: string | null;
    avatar_url: string | null;
  } | null;
}

const PostDetail = () => {
  const { postId } = useParams<{ postId: string }>();
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [post, setPost] = useState<Post | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [isLiked, setIsLiked] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [conversations, setConversations] = useState<any[]>([]);
  const [loadingConversations, setLoadingConversations] = useState(false);

  useEffect(() => {
    if (postId) {
      fetchPost();
      fetchComments();
      checkIfLiked();
      checkIfSaved();
    }
  }, [postId, user]);

  const fetchPost = async () => {
    if (!postId) return;

    try {
      // Fetch post with pet info only
      const { data, error } = await supabase
        .from('posts')
        .select(`
          *,
          pets (name, avatar_url)
        `)
        .eq('id', postId)
        .maybeSingle();

      if (error) throw error;
      if (!data) {
        setPost(null);
        return;
      }

      // Fetch author profile separately (no direct FK available for join)
      const { data: profileData } = await supabase
        .from('profiles')
        .select('username, display_name, avatar_url')
        .eq('user_id', (data as any).user_id)
        .maybeSingle();

      const merged = {
        ...(data as any),
        profiles: profileData || null,
      } as Post;

      setPost(merged);
    } catch (error) {
      console.error('Error fetching post:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    if (!postId) return;

    try {
      const { data, error } = await supabase
        .from('post_comments')
        .select('*')
        .eq('post_id', postId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const commentsData = (data as any[]) || [];
      const userIds = Array.from(new Set(commentsData.map((c: any) => c.user_id).filter(Boolean)));

      let profilesMap: Record<string, any> = {};
      if (userIds.length > 0) {
        const { data: profilesData } = await supabase
          .from('profiles')
          .select('user_id, username, display_name, avatar_url')
          .in('user_id', userIds as any);
        profilesMap = Object.fromEntries(
          (profilesData || []).map((p: any) => [p.user_id, {
            username: p.username,
            display_name: p.display_name,
            avatar_url: p.avatar_url,
          }])
        );
      }

      const withProfiles = commentsData.map((c: any) => ({
        ...c,
        profiles: profilesMap[c.user_id] || null,
      }));

      setComments(withProfiles as any);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const checkIfLiked = async () => {
    if (!postId || !user) return;

    try {
      const { data } = await supabase
        .from('post_likes')
        .select('id')
        .eq('post_id', postId)
        .eq('user_id', user.id)
        .maybeSingle();

      setIsLiked(!!data);
    } catch (error) {
      console.error('Error checking if liked:', error);
      setIsLiked(false);
    }
  };

  const checkIfSaved = async () => {
    if (!postId || !user) return;

    try {
      const { data } = await supabase
        .from('post_saves')
        .select('id')
        .eq('post_id', postId)
        .eq('user_id', user.id)
        .maybeSingle();

      setIsSaved(!!data);
    } catch (error) {
      console.error('Error checking if saved:', error);
      setIsSaved(false);
    }
  };

  const handleLike = async () => {
    if (!post || !user) return;

    try {
      if (isLiked) {
        const { error } = await supabase
          .from('post_likes')
          .delete()
          .eq('post_id', post.id)
          .eq('user_id', user.id);
        
        if (error) throw error;
        setIsLiked(false);
      } else {
        const { error } = await supabase
          .from('post_likes')
          .insert({
            post_id: post.id,
            user_id: user.id,
          });

        if (error) throw error;
        setIsLiked(true);
      }
      
      // Refresh the post to get updated count from database
      await fetchPost();
    } catch (error) {
      console.error('Error handling like:', error);
    }
  };

  const handleSave = async () => {
    if (!post || !user) return;

    try {
      if (isSaved) {
        const { error } = await supabase
          .from('post_saves')
          .delete()
          .eq('post_id', post.id)
          .eq('user_id', user.id);
        
        if (error) throw error;
        setIsSaved(false);
        toast.success('Post rimosso dai salvati');
      } else {
        const { error } = await supabase
          .from('post_saves')
          .insert({
            post_id: post.id,
            user_id: user.id,
          });

        if (error) throw error;
        setIsSaved(true);
        toast.success('Post salvato');
      }
    } catch (error) {
      console.error('Error handling save:', error);
      toast.error('Errore nel salvare il post');
    }
  };

  const handleComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || !post || !user) return;

    setSubmittingComment(true);
    try {
      const { data, error } = await supabase
        .from('post_comments')
        .insert({
          post_id: post.id,
          user_id: user.id,
          content: newComment.trim(),
        })
        .select('*')
        .single();

      if (error) throw error;

      // Fetch the current user's profile for display
      const { data: profileData } = await supabase
        .from('profiles')
        .select('username, display_name, avatar_url')
        .eq('user_id', user.id)
        .maybeSingle();

      const enriched = {
        ...(data as any),
        profiles: profileData || null,
      } as Comment;

      setComments([...comments, enriched]);
      setNewComment('');
      
      // Refresh the post to get updated comment count from database
      await fetchPost();
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Post di ${post?.profiles?.display_name || post?.profiles?.username}`,
          text: post?.content || 'Guarda questo post!',
          url: url,
        });
      } catch (error) {
        // User cancelled or share failed, fallback to copy
        copyToClipboard(url);
      }
    } else {
      copyToClipboard(url);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Link copiato negli appunti!');
    }).catch(() => {
      toast.error('Errore nel copiare il link');
    });
  };

  const handleDeletePost = async () => {
    if (!post || !user || post.user_id !== user.id) return;
    
    if (window.confirm('Sei sicuro di voler eliminare questo post?')) {
      try {
        const { error } = await supabase
          .from('posts')
          .delete()
          .eq('id', post.id);
        
        if (error) throw error;
        
        toast.success('Post eliminato con successo');
        window.history.back();
      } catch (error) {
        console.error('Error deleting post:', error);
        toast.error('Errore nell\'eliminare il post');
      }
    }
  };

  const handleReportPost = () => {
    toast.info('Funzionalità di segnalazione in arrivo');
  };

  const fetchConversations = async () => {
    if (!user) return;
    
    setLoadingConversations(true);
    try {
      // First get conversations where user is a participant (same approach as Messages.tsx)
      const { data: userConversations, error } = await supabase
        .from('conversation_participants')
        .select('conversation_id')
        .eq('user_id', user.id)
        .is('left_at', null);

      if (error) throw error;

      const conversationIds = userConversations?.map(item => item.conversation_id) || [];

      if (conversationIds.length > 0) {
        // Get conversations details
        const { data: conversationsData, error: convError } = await supabase
          .from('conversations')
          .select('*')
          .in('id', conversationIds)
          .order('last_message_at', { ascending: false });

        if (convError) throw convError;

        // Get participants for each conversation
        const conversationsWithParticipants = await Promise.all(
          (conversationsData || []).map(async (conv) => {
            const { data: participantIds } = await supabase
              .from('conversation_participants')
              .select('user_id')
              .eq('conversation_id', conv.id)
              .is('left_at', null);

            const participants = await Promise.all(
              (participantIds || []).map(async (p) => {
                const { data: profile } = await supabase
                  .from('profiles')
                  .select('user_id, username, display_name, avatar_url')
                  .eq('user_id', p.user_id)
                  .single();

                return {
                  user_id: p.user_id,
                  profiles: profile || { user_id: '', username: '', display_name: null, avatar_url: null }
                };
              })
            );

            return {
              ...conv,
              type: conv.type as 'direct' | 'group',
              participants: participants
            };
          })
        );

        setConversations(conversationsWithParticipants);
      } else {
        setConversations([]);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setConversations([]);
    } finally {
      setLoadingConversations(false);
    }
  };

  const handleShareToChat = async (conversationId: string) => {
    if (!user || !post) return;

    try {
      const postUrl = window.location.href;
      const message = `Dai un'occhiata a questo post: ${postUrl}`;

      const { error } = await supabase
        .from('messages')
        .insert({
          conversation_id: conversationId,
          sender_id: user.id,
          content: message,
          message_type: 'text'
        });

      if (error) throw error;

      toast.success('Post condiviso in chat!');
      setShowShareModal(false);
    } catch (error) {
      console.error('Error sharing to chat:', error);
      toast.error('Errore nel condividere il post');
    }
  };

  const openShareModal = () => {
    setShowShareModal(true);
    fetchConversations();
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto p-4 space-y-4">
        <div className="h-8 w-32 bg-muted rounded animate-pulse" />
        <Card className="glass border-white/20">
          <CardHeader>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-muted rounded-full animate-pulse" />
              <div className="space-y-2">
                <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                <div className="h-3 bg-muted rounded w-16 animate-pulse" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-4 bg-muted rounded w-3/4 mb-4 animate-pulse" />
            <div className="h-48 bg-muted rounded animate-pulse" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <Link to="/app/profile">
          <Button variant="ghost" className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Indietro
          </Button>
        </Link>
        <div className="text-center py-16">
          <MessageCircle className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Post non trovato</h3>
          <p className="text-muted-foreground">
            Non è stato possibile trovare questo post.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="cosmic-background min-h-screen"
      style={{ backgroundImage: `url(${feedHeroBg})` }}
    >
      <div className="max-w-2xl mx-auto p-4 pb-20 lg:pb-4">
      {/* Back Button */}
      <Link to={window.history.length > 1 ? -1 as any : "/app/profile"}>
        <Button variant="ghost" className="mb-4 text-white hover:text-primary">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Indietro
        </Button>
      </Link>

      {/* Post Card */}
      <Card className="glass border-white/20 mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div 
              className="flex items-center space-x-3 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => navigate(`/app/user/${post.profiles?.username}`)}
            >
              <Avatar>
                <AvatarImage src={post.profiles?.avatar_url || undefined} />
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {post.profiles?.username.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-semibold text-white hover:text-primary transition-colors">
                  {post.profiles?.display_name || post.profiles?.username}
                </p>
                <p className="text-sm text-muted-foreground">
                  {formatDistanceToNow(new Date(post.created_at), { 
                    addSuffix: true, 
                    locale: it 
                  })}
                  {post.pets && (
                    <> • con <span className="text-primary">{post.pets.name}</span></>
                  )}
                </p>
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="glass border-white/20">
                {post.user_id === user?.id ? (
                  <>
                    <DropdownMenuItem onClick={handleDeletePost} className="text-red-500 focus:text-red-500">
                      <Trash2 className="w-4 h-4 mr-2" />
                      Elimina post
                    </DropdownMenuItem>
                  </>
                ) : (
                  <DropdownMenuItem onClick={handleReportPost}>
                    <Flag className="w-4 h-4 mr-2" />
                    Segnala post
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={openShareModal}>
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Condividi in chat
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => copyToClipboard(window.location.href)}>
                  <Copy className="w-4 h-4 mr-2" />
                  Copia link
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {/* Content */}
          {post.content && (
            <p className="text-white">{post.content}</p>
          )}

          {/* Image */}
          {post.image_url && (
            <img 
              src={post.image_url} 
              alt="Post" 
              className="w-full rounded-lg"
            />
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-white/10">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLike}
                className={`${isLiked ? 'text-red-500' : 'text-muted-foreground'} hover:text-red-500`}
              >
                <Heart className={`w-5 h-5 mr-2 ${isLiked ? 'fill-current' : ''}`} />
                {post.likes_count}
              </Button>
              <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-primary">
                <MessageCircle className="w-5 h-5 mr-2" />
                {post.comments_count}
              </Button>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleSave}
              className={`${isSaved ? 'text-primary' : 'text-muted-foreground'} hover:text-primary`}
            >
              {isSaved ? <BookmarkCheck className="w-5 h-5" /> : <Bookmark className="w-5 h-5" />}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Comments Section */}
      <Card className="glass border-white/20">
        <CardHeader>
          <h3 className="text-lg font-semibold text-white">
            Commenti ({comments.length})
          </h3>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {/* Add Comment */}
          <form onSubmit={handleComment} className="flex gap-3">
            <Avatar className="w-8 h-8">
              <AvatarImage src={user?.user_metadata?.avatar_url} />
              <AvatarFallback className="bg-gradient-to-br from-primary to-secondary text-white font-bold text-sm">
                {profile?.display_name?.charAt(0).toUpperCase() || profile?.username?.charAt(0).toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 flex gap-2">
              <Input
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Aggiungi un commento..."
                className="glass border-white/20 text-white"
                disabled={submittingComment}
              />
              <Button 
                type="submit" 
                size="sm" 
                disabled={!newComment.trim() || submittingComment}
                className="bg-primary hover:bg-primary/80"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </form>

          {/* Comments List */}
          <div className="space-y-4">
            {comments.map((comment) => (
              <div key={comment.id} className="flex gap-3">
                <Avatar className="w-8 h-8">
                  <AvatarImage src={comment.profiles?.avatar_url || undefined} />
                  <AvatarFallback className="bg-secondary text-secondary-foreground text-sm">
                    {comment.profiles?.username.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="bg-muted/10 rounded-lg p-3">
                    <p className="font-semibold text-sm text-white">
                      {comment.profiles?.display_name || comment.profiles?.username}
                    </p>
                    <p className="text-white mt-1">{comment.content}</p>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatDistanceToNow(new Date(comment.created_at), { 
                      addSuffix: true, 
                      locale: it 
                    })}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {comments.length === 0 && (
            <div className="text-center py-8">
              <MessageCircle className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
              <p className="text-muted-foreground">Nessun commento ancora</p>
              <p className="text-sm text-muted-foreground">Sii il primo a commentare!</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Share to Chat Modal */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="glass border-white/20 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Condividi in chat</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {loadingConversations ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="flex items-center space-x-3 p-3">
                    <div className="w-10 h-10 bg-muted rounded-full animate-pulse" />
                    <div className="flex-1 space-y-1">
                      <div className="h-4 bg-muted rounded w-3/4 animate-pulse" />
                      <div className="h-3 bg-muted rounded w-1/2 animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            ) : conversations.length === 0 ? (
              <div className="text-center py-8">
                <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
                <p className="text-muted-foreground">Nessuna chat disponibile</p>
                <p className="text-sm text-muted-foreground">Inizia una conversazione per condividere post</p>
              </div>
            ) : (
              conversations.map((conv) => {
                // Get conversation name (same logic as Messages.tsx)
                const getConversationName = () => {
                  if (conv.type === 'group') {
                    return conv.name || 'Chat di gruppo';
                  }
                  const otherParticipant = conv.participants?.find((p: any) => p.user_id !== user?.id);
                  return otherParticipant?.profiles?.display_name || otherParticipant?.profiles?.username || 'Chat';
                };

                const getConversationAvatar = () => {
                  if (conv.type === 'group') {
                    return conv.avatar_url;
                  }
                  const otherParticipant = conv.participants?.find((p: any) => p.user_id !== user?.id);
                  return otherParticipant?.profiles?.avatar_url;
                };

                return (
                  <div
                    key={conv.id}
                    onClick={() => handleShareToChat(conv.id)}
                    className="flex items-center space-x-3 p-3 rounded-lg hover:bg-white/5 cursor-pointer transition-colors"
                  >
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={getConversationAvatar() || undefined} />
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        {getConversationName().charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="font-medium text-white">
                        {getConversationName()}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {conv.type === 'direct' ? 'Chat diretta' : 'Gruppo'}
                      </p>
                    </div>
                    <Send className="w-4 h-4 text-muted-foreground" />
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default PostDetail;